import React, { useEffect, useRef } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useTrigger } from '@atlassian/post-office-context';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useGetData } from './services/get-data/useGetData.tsx';
import { asyncChangeboardingFlagEntryPoint } from './ui/flag/entrypoint.tsx';
import { asyncChangeboardingModalEntryPoint } from './ui/modal/entrypoint.tsx';
import { emitFlagMessageEvent, emitModalMessageEvent } from './common/utils.tsx';
import { FLAG_MESSAGE_TRIGGER_ID, MODAL_MESSAGE_TRIGGER_ID } from './common/constants.tsx';

const entryPointParams = {};

type PublicFormChangeboardingModalContainerProps = {
	projectId: string;
};

const PublicFormChangeboardingModalRoot = (props: PublicFormChangeboardingModalContainerProps) => {
	return (
		<JSErrorBoundary
			id="public-forms-changeboarding-modal"
			packageName="@atlassian/jira-public-forms-changeboarding-modal"
			teamName="a4t-tanuki"
			fallback="unmount"
		>
			<ApolloClientProvider client={client}>
				<ModalTransition>
					<PublicFormChangeboardingModalContainer {...props} />
				</ModalTransition>
			</ApolloClientProvider>
		</JSErrorBoundary>
	);
};

const PublicFormChangeboardingModalContainer = ({
	projectId,
}: PublicFormChangeboardingModalContainerProps) => {
	const { isActive: isModalTriggerActive } = useTrigger(MODAL_MESSAGE_TRIGGER_ID);
	const { isActive: isFlagTriggerActive } = useTrigger(FLAG_MESSAGE_TRIGGER_ID);

	const data = useGetData({
		projectId,
		isEnabled:
			(isModalTriggerActive || isFlagTriggerActive) &&
			fg('jira_forms_public_access_changeboarding_gate'),
	});
	const hasLoadedEntrypoint = useRef(false);

	const {
		entryPointActions: modalEntryPointActions,
		entryPointReferenceSubject: modalEntryPointReferenceSubject,
	} = useEntryPoint(asyncChangeboardingModalEntryPoint, entryPointParams);

	const {
		entryPointActions: flagEntryPointActions,
		entryPointReferenceSubject: flagEntryPointReferenceSubject,
	} = useEntryPoint(asyncChangeboardingFlagEntryPoint, entryPointParams);

	const isEligibleToViewChangeboarding =
		data &&
		!data.projectHasPublicForm &&
		data.isPublicFormEnabledForSite &&
		data.canAdministerProject &&
		data.projectHasFormNavItem;

	const shouldLoadModal = isModalTriggerActive && isEligibleToViewChangeboarding;
	const shouldRenderFlag = isFlagTriggerActive && isEligibleToViewChangeboarding;

	useEffect(() => {
		// if project already has public forms or has been disabled for the site, then
		// they already are aware of the feature and do not need to be notified
		if (data?.projectHasPublicForm || data?.isPublicFormEnabledForSite === false) {
			emitModalMessageEvent({ action: 'filter' });
			emitFlagMessageEvent({ action: 'filter' });
		}
	}, [data?.projectHasPublicForm, data?.isPublicFormEnabledForSite]);

	useEffect(() => {
		if (!hasLoadedEntrypoint.current) {
			if (shouldRenderFlag) {
				if (!expVal('jira_forms_public_access_changeboarding', 'isEnabled', false)) {
					// if in control cohort, snooze the post office message and check again later
					// as experiment gradually rolls out
					emitFlagMessageEvent({ action: 'snooze' });
					return;
				}

				flagEntryPointActions.load();
				hasLoadedEntrypoint.current = true;
			}

			if (shouldLoadModal) {
				if (!expVal('jira_forms_public_access_changeboarding', 'isEnabled', false)) {
					emitModalMessageEvent({ action: 'snooze' });
					return;
				}

				modalEntryPointActions.load();
				hasLoadedEntrypoint.current = true;
			}
		}
	}, [flagEntryPointActions, modalEntryPointActions, shouldLoadModal, shouldRenderFlag]);

	if (!data || !data.project || (!shouldLoadModal && !shouldRenderFlag)) {
		return null;
	}

	const firstFormIdInProject = data.forms?.[0]?.formId;

	return (
		<>
			<JiraEntryPointContainer
				entryPointReferenceSubject={modalEntryPointReferenceSubject}
				id="public-forms-changeboarding-modal"
				packageName="@atlassian/jira-public-forms-changeboarding-modal"
				teamName="a4t-tanuki"
				fallback={null}
				errorFallback="unmount"
				runtimeProps={{ firstFormIdInProject, project: data.project }}
			/>
			<JiraEntryPointContainer
				entryPointReferenceSubject={flagEntryPointReferenceSubject}
				id="public-forms-changeboarding-flag"
				packageName="@atlassian/jira-public-forms-changeboarding-modal"
				teamName="a4t-tanuki"
				fallback={null}
				errorFallback="unmount"
				runtimeProps={{ firstFormIdInProject, project: data.project }}
			/>
		</>
	);
};

export default PublicFormChangeboardingModalRoot;
